import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, Wrench, MessageSquare,
  Sparkles, Building2, LogOut, Menu, X,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/admin/hooks/useAuth";

const AdminLayout = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [unread, setUnread]           = useState({ messages: 0, dreams: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar when route changes (mobile nav tap)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    const fetchUnread = async () => {
      const [msgs, drms] = await Promise.all([
        supabase.from("contact_messages").select("id", { count: "exact", head: true }).eq("is_read", false).eq("is_archived", false),
        supabase.from("dream_requests").select("id",   { count: "exact", head: true }).eq("is_read", false).eq("is_archived", false),
      ]);
      setUnread({ messages: msgs.count ?? 0, dreams: drms.count ?? 0 });
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60_000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  };

  const NAV = [
    { to: "/admin",                   label: "Ana Sayfa",          icon: LayoutDashboard, end: true                  },
    { to: "/admin/projeler",          label: "Projeler",           icon: FolderOpen                                   },
    { to: "/admin/hizmetler",         label: "Hizmetler",          icon: Wrench                                       },
    { to: "/admin/mesajlar",          label: "Mesajlar",           icon: MessageSquare,   badge: unread.messages      },
    { to: "/admin/hayalim-talepleri", label: "Hayalim Talepleri",  icon: Sparkles,        badge: unread.dreams        },
    { to: "/admin/sirket",            label: "Şirket Bilgileri",   icon: Building2                                    },
  ];

  const totalUnread = unread.messages + unread.dreams;

  return (
    <div className="min-h-screen bg-[#060606] flex">

      {/* ── Mobile backdrop ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside className={`
        fixed md:static inset-y-0 left-0 z-40
        w-64 min-h-screen bg-[#0d0d0d] border-r border-white/8 flex flex-col
        transition-transform duration-250 ease-out md:translate-x-0
        ${sidebarOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}
      `}>

        {/* Brand header */}
        <div className="px-5 py-5 border-b border-white/8 flex items-center justify-between">
          <div>
            <p className="text-[#C5A059] text-[11px] tracking-[0.2em] uppercase font-semibold">
              Konyap Mühendislik
            </p>
            <p className="text-[#F5F5F7] text-[15px] font-light mt-0.5">Yönetim Paneli</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-2 text-[#555] hover:text-[#F5F5F7] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3.5 px-3.5 py-3 text-[14px] font-light rounded-sm transition-colors duration-150 ${
                  isActive
                    ? "text-[#C5A059] bg-[#C5A059]/10 font-medium"
                    : "text-[#888] hover:text-[#F5F5F7] hover:bg-white/5"
                }`
              }
            >
              <Icon size={17} strokeWidth={1.5} className="flex-shrink-0" />
              <span className="flex-1">{label}</span>
              {badge > 0 && (
                <span className="bg-[#C5A059] text-black text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User + logout */}
        <div className="px-3 py-4 border-t border-white/8">
          {user?.email && (
            <p className="px-3.5 text-[11px] text-[#444] mb-2 truncate">{user.email}</p>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3.5 px-3.5 py-3 text-[14px] font-light text-[#666] hover:text-red-400 hover:bg-red-400/6 rounded-sm transition-colors duration-150"
          >
            <LogOut size={16} strokeWidth={1.5} />
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-20 bg-[#0d0d0d] border-b border-white/8 flex items-center gap-3 px-4 h-14 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-1 text-[#888] hover:text-[#F5F5F7] transition-colors"
          >
            <Menu size={22} />
          </button>
          <span className="text-[#F5F5F7] text-[15px] font-light">Konyap Yönetim</span>
          {totalUnread > 0 && (
            <span className="ml-auto bg-[#C5A059] text-black text-[11px] font-bold px-2.5 py-0.5 rounded-full">
              {totalUnread} yeni
            </span>
          )}
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 md:p-8 max-w-5xl w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
